import {useEffect} from 'react'
import {useToast} from './use-toast'
import {useAddToCartModalContext} from './use-add-to-cart-modal'
import {useBonusProductsModalContext} from './use-bonus-products-modal'
import {trackAddProductSetToCart, trackAddToCart} from '../tracking/analytics'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '../constants'
import useBasket from '../commerce-api/hooks/useBasket'
import useEinstein from '../commerce-api/hooks/useEinstein'
import {useProduct} from './use-product'
import {useProductSpecialOffers} from '../components/special-offer/product-special-offers'
import {useSpecialOffer} from '../components/special-offer'
import {getProductAddToBasketItems, isProductSet} from '../utils/product-utils'
import useEffectEvent from './use-effect-event'

export const useAddToCart = (
    product,
    {
        isLoading: isProductLoading,
        promotions: preloadedPromotions,
        showAddToCartModal = true,
        onRecommendedProductClick
    } = {}
) => {
    const showToast = useToast()
    const isProductASet = isProductSet(product)
    const location = useLocation()
    const {formatMessage} = useIntl()
    const basket = useBasket()
    const einstein = useEinstein()
    const {variant, isOrderable, setIncludesBonusProduct} = useProduct(product)
    // get all promotions available for the product
    const {promotions, loading: productSpecialOffersLoading} = useProductSpecialOffers(
        product,
        null,
        null,
        {isLoading: isProductLoading, promotions: preloadedPromotions}
    )
    // if we have multiple promotions for the product - use only first one
    const {
        products: bonusProducts,
        colorScheme,
        fontColor,
        loading: specialOffersLoading
    } = useSpecialOffer(promotions?.find((p) => p?.c_discountProducts?.length > 0))
    const isLoading = isProductLoading || productSpecialOffersLoading || specialOffersLoading
    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
        data,
        addProduct
    } = useAddToCartModalContext()

    const {isOpen: isBonusProductsModalOpen, onOpen: onBonusProductsModalOpen} =
        useBonusProductsModalContext() || {}

    const onLocationChange = useEffectEvent(() => {
        if (isAddToCartModalOpen) {
            onAddToCartModalClose()
        }
    })
    useEffect(() => {
        onLocationChange(location.pathname)
    }, [location.pathname, onLocationChange])

    // used to display add to cart overlay
    const onBonusProductsModalClose = useEffectEvent(() => {
        onAddToCartModalOpen(onRecommendedProductClick)
    })
    useEffect(() => {
        if (showAddToCartModal && !isBonusProductsModalOpen && data) {
            onBonusProductsModalClose()
        }
    }, [isBonusProductsModalOpen, showAddToCartModal, data, onBonusProductsModalClose])

    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const handleAddToCart = async ({onAfterAddToCart, engraveText} = {}) => {
        try {
            if (!isOrderable || isLoading) return
            const productItems = getProductAddToBasketItems(product, engraveText)
            if (!productItems?.length) return

            const updatedBasket = await basket?.addItemToBasket(productItems)
            if (onAfterAddToCart) {
                onAfterAddToCart()
            }
            // einstein
            productItems.map((item) =>
                einstein.sendAddToCart({
                    ...item,
                    masterId: product?.master?.masterId
                })
            )
            // analytics
            if (isProductASet) {
                trackAddProductSetToCart(product, basket)
            } else {
                trackAddToCart(
                    {
                        ...product,
                        productName: variant?.name || product?.name
                    },
                    basket
                )
            }

            // if product has bonus product promotions show bonus products modal first - then add to cart modal
            const variationKey = product.master ? product.master.masterId : product.id
            const isCurrentProductBonus =
                promotions?.[0]?.c_discountVariantProducts?.[variationKey]?.indexOf(product.id) >= 0
            // if we have multiple promotions for the product - use only first one and only in case it is already applied to the basket
            const appliedBonusPromotions = updatedBasket?.bonusDiscountLineItems?.filter(
                (bonus) => bonus.promotionId === promotions?.[0]?.id
            )
            if (
                appliedBonusPromotions &&
                bonusProducts &&
                !isBonusProductsModalOpen &&
                !isCurrentProductBonus &&
                !setIncludesBonusProduct
            ) {
                onBonusProductsModalOpen({
                    products: bonusProducts?.data,
                    bonusProductVariationsMap: promotions?.[0]?.c_discountVariantProducts,
                    colorScheme,
                    fontColor,
                    showAddToCartModal: true,
                    setSelectedItem: () => {}
                })
            }

            // add current product to add to cart modal
            addProduct(product)
        } catch (error) {
            console.error(error)
            showError()
        }
    }

    return {
        handleAddToCart,
        isLoading
    }
}
